/**
 * Полоса этапов посева: Запланировано → Растёт → Убрано (AK-509, ADR-059 §3).
 *
 * AK-434 (запрос founder, UAT 2026-07-28): полоса была read-only `<ol>`, и на
 * карточке поля не было НИ ОДНОГО способа сдвинуть статус — агроном видел
 * прогресс, но не мог им управлять. Теперь шаг — тап; дёргается тот же
 * `PATCH /fields/{id}/occupancy/{sowing_id} {status}`, что и кнопка «Отметить:
 * посеяно» в попапе карты (один контракт, одна инвалидация).
 *
 * AK-509: раньше полоса рисовала ПЯТЬ шагов (План · Посеян · Вегетация ·
 * Убирают · Убран, PR #573 / ADR-055 §D1 поправка 28.07) — founder 30.07
 * утвердил 4-статусную модель (ADR-059, wireframe #601 v4). «Посеян» и
 * «Убирают» (частичная уборка) больше не отдельные шаги: сев — это событие
 * (кнопка «Отметить: посеяно» остаётся, её механику не трогаем), а частичная
 * уборка — цифра «убрано X из Y га» РЯДОМ со статусом «Растёт», не пятый pill
 * (`sowingStageQuantity`, ADR-059 §4a.5).
 *
 * Статус ОДИН (поправка к ADR-055 §D1): его сводит сервер
 * (`crop_status_resolved`), рука перебивает автомат. Полоса ГРУБЕЕ статуса —
 * «Посеян частично», «Посеян» и «Убирают» это один и тот же шаг («Растёт»), —
 * поэтому подпись ТЕКУЩЕГО шага берётся из статуса, а не из имени шага: бейдж
 * рядом и полоса печатают одно слово.
 *
 * Чего полоса НЕ делает (review head-web):
 *   · не рисуется для пара и для исходов сезона («Погиб», «Списано», «Запахан»,
 *     «Закрыт», «Пар завершён»). Раньше такой посев подсвечивал «План» — тот же
 *     дефект, что PR чинит, — и его шаги оставались кнопками, т.е. погибший
 *     посев воскресал в «Убран»;
 *   · не прячет отмену: назад тапом можно (решение founder 28.07), поэтому
 *     промах по мелкой цели лечится там же, где случился. Осознанный возврат с
 *     ПРИЧИНОЙ живёт в меню статуса посева; оба пути пишут в один журнал.
 */
import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ApiError } from '@/lib/api';
import { toast } from '@/lib/toast';
import { useSeasonGuard } from '@/lib/season/SeasonContext';
import {
  ALLOW_STAGE_ROLLBACK,
  SOWING_STAGE_LABELS,
  SOWING_STAGE_LADDER,
  isLadderStage,
  sowingStage,
  sowingStageIndex,
  sowingStageLabel,
  sowingStageQuantity,
  type SowingQuantitySource,
  type SowingStageCode,
} from '@/lib/sowings/stage';
import { usePatchOccupancy } from './api';

interface Props {
  /** Занятие leaf'а. Статус считается из него (`crop_status`, иначе `status`). */
  sowing?: SowingQuantitySource | null;
  /** Нужен для мутации. Не передан → полоса read-only. */
  fieldId?: string;
  /** Право на правку посевов. Нет → read-only. */
  canWrite?: boolean;
  className?: string;
}

export function OccupancyProgress({
  sowing,
  fieldId,
  canWrite = false,
  className,
}: Props) {
  const serverStage = sowingStage(sowing);
  const seasonGuard = useSeasonGuard();
  const patch = usePatchOccupancy(fieldId ?? '');
  const [pending, setPending] = useState<string | null>(null);
  // Оптимистичный статус: полоса едет сразу по тапу и держится, пока сервер не
  // догонит (инвалидация ['field-card'] → refetch occupancy). Иначе между
  // ответом PATCH и приездом свежих данных шаг мигал бы обратно.
  const [optimistic, setOptimistic] = useState<string | null>(null);
  useEffect(() => {
    if (optimistic && serverStage === optimistic) setOptimistic(null);
  }, [optimistic, serverStage]);

  // Занятия ещё нет (форма посева до сохранения) → показываем лестницу с
  // «Планом» как превью того, что создастся. Нажать нечего: sowing_id нет.
  const stage = optimistic ?? serverStage ?? 'planned';

  const sowingId =
    sowing && 'sowing_id' in sowing
      ? ((sowing as { sowing_id?: string }).sowing_id ?? null)
      : null;

  async function moveTo(code: SowingStageCode) {
    if (!sowingId) return;
    setPending(code);
    setOptimistic(code);
    try {
      await patch.mutateAsync({ sowingId, body: { status: code } });
    } catch (err) {
      setOptimistic(null);
      toast.error(
        err instanceof ApiError ? err.detail : 'Не удалось изменить статус',
      );
    } finally {
      setPending(null);
    }
  }

  // Пар лестницей не описывается — «План → Посеян → Убран» к нему неприменимо.
  // Проверяем сохранённое ЗАНЯТИЕ, а не состояние формы: в SowForm блок
  // «Прогресс» гейтится по выбранному виду занятия, и существующий пар,
  // переключённый на «Культуру» до сохранения, дотягивался бы сюда с
  // sowing_id пара.
  if (sowing?.occupancy_type === 'fallow' || sowing?.is_fallow) return null;

  // Исход сезона — печатаем ФАКТИЧЕСКОЕ слово и ничего не предлагаем нажать.
  // Полоса из пяти шагов тут врала бы дважды: подсветкой «Плана» и кнопками,
  // воскрешающими посев.
  if (!isLadderStage(stage)) {
    return (
      <div className={cn('space-y-1', className)} data-testid="stage-off-ladder">
        <span className="inline-flex items-center rounded-full bg-muted px-3 py-1 text-xs text-muted-foreground">
          {sowingStageLabel({ crop_status: stage })}
        </span>
        <p className="text-xs leading-tight text-muted-foreground">
          Этот посев уже снят с обычного хода сезона. Поменять — в меню статуса
          посева, там записывается причина.
        </p>
      </div>
    );
  }

  const current = sowingStageIndex(stage);
  const interactive =
    canWrite && Boolean(fieldId) && Boolean(sowingId) && seasonGuard.canWrite;
  // AK-509: частичное — цифрой, не пятым pill'ом (ADR-059 §4a.5). `sowing`
  // несёт площади с backend'а (`crop_status_resolved`); optimistic-статус
  // площадей не меняет — цифра остаётся от последнего ответа сервера, что
  // корректно (площадь не зависит от локального тапа по лестнице).
  const quantity = sowingStageQuantity(sowing);

  return (
    <div className={cn('space-y-1', className)}>
      <ol
        className="flex flex-wrap items-center gap-1"
        aria-label="Этапы посева"
      >
        {SOWING_STAGE_LADDER.map((code, i) => {
          const isCurrent = i === current;
          // Текущий шаг подписан САМИМ статусом (уже свёрнутым в 4 значения
          // словарём, ADR-059 §3): «Растёт» вместо «Посеян частично»/«Убирают»,
          // «Убрано» вместо «Запахан». Так полоса и бейдж рядом говорят одно
          // слово.
          const label = isCurrent
            ? sowingStageLabel({ crop_status: stage })
            : SOWING_STAGE_LABELS[code];
          const done = i < current;
          // Вперёд можно всегда; назад — под общим флагом отката (решение
          // founder). Флаг зеркалит гейт backend'а: иначе UI предлагал бы шаг,
          // на который сервер отвечает 422.
          const clickable =
            interactive && !isCurrent && (i > current || ALLOW_STAGE_ROLLBACK);
          const busy = pending === code;
          const chip = (
            <>
              <span
                aria-hidden
                className={cn(
                  'h-1.5 w-1.5 rounded-full',
                  done && 'bg-primary',
                  isCurrent && 'bg-primary-foreground',
                  !done && !isCurrent && 'bg-muted-foreground/50',
                )}
              />
              {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {label}
            </>
          );
          const chipClass = cn(
            'inline-flex items-center gap-2 rounded-full text-xs transition-colors',
            // Некликабельный чип — компактный; интерактивный обязан быть не
            // меньше 32 px по высоте: рабочая поверхность это планшет в поле,
            // палец, иногда перчатка (WCAG 2.2 target size, CLAUDE.md).
            clickable ? 'min-h-8 px-3 py-1' : 'px-2 py-1',
            done && 'bg-primary/15 text-primary',
            isCurrent && 'bg-primary text-primary-foreground font-medium',
            !done && !isCurrent && 'bg-muted text-muted-foreground',
            clickable &&
              'cursor-pointer hover:bg-primary/20 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          );
          return (
            <li key={code} className="flex items-center gap-1">
              {clickable ? (
                <button
                  type="button"
                  className={chipClass}
                  disabled={pending !== null}
                  aria-busy={busy || undefined}
                  aria-label={
                    i < current
                      ? `Вернуть этап: ${SOWING_STAGE_LABELS[code]}`
                      : `Отметить этап: ${SOWING_STAGE_LABELS[code]}`
                  }
                  onClick={() => void moveTo(code)}
                >
                  {chip}
                </button>
              ) : (
                <span
                  className={chipClass}
                  aria-current={isCurrent ? 'step' : undefined}
                >
                  {chip}
                </span>
              )}
              {i < SOWING_STAGE_LADDER.length - 1 && (
                <span aria-hidden className="h-px w-3 bg-border" />
              )}
            </li>
          );
        })}
      </ol>
      {/* AK-509: частичная уборка/сев — цифра рядом со статусом «Растёт», не
          пятый pill (ADR-059 §4a.5). */}
      {quantity && (
        <p className="text-xs text-muted-foreground" data-testid="stage-quantity">
          {quantity}
        </p>
      )}
      {/* Причина блокировки — видимой строкой, не тултипом: на планшете в поле
          hover'а нет (CLAUDE.md), а «полоса не нажимается» без объяснения
          читается как поломка. */}
      {canWrite && Boolean(fieldId) && !seasonGuard.canWrite && (
        <p className="text-xs text-muted-foreground" data-testid="stage-locked-reason">
          {seasonGuard.reason}
        </p>
      )}
    </div>
  );
}
